import {
  ICameraInfo,
  SemanticDarkColors,
  ENABLED_CLOUD_OLD_MODELS,
  OLD_MODELS,
  LightColors,
} from '@thingsw/pitta-modules';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../features/store';
import { THEME } from '../../features/Theme/slice';
import { Typography, Button } from '@thingsw/pitta-design-system';
import { useTranslation } from 'react-i18next';

import { getRenewalCloudConnectivityIcon } from '../../utils/Camera';
import { BatteryStatusButton } from '../battery/BatteryStatusButton';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { RenewalCameraMenu } from './RenewalCameraMenu';
import _ from 'lodash';
import { USER } from '../../features/User/slice';
import { APP_MENU } from '../../screens/app/renewal2/RenewalMapviewScreen';
import { PERMISSION } from '../../features/Permission/slice';

const useStyles = makeStyles((theme: Theme) => ({
  BatteryStatusButton: {
    display: 'flex',
    alignItems: 'center',
    ...(theme.direction === 'rtl' ? { marginRight: 15 } : { marginLeft: 15 }),
  },
  btnContainer: {
    display: 'flex',
    padding: 2,
    gap: 4,
  },
  btn: {
    flex: 1,
    height: 80,
    border: (props: any) => `1px solid ${props.colors.primary['6']}`,
    borderRadius: 8,
    padding: 8,
    gap: 4,
    display: 'flex',
    flexDirection: 'column',
  },
  rootTransition: {
    transition: theme.transitions.create(['transform', 'height']),
    userSelect: 'none',
  },
  panelClose: {
    height: 130,
  },
  panelOpen: {
    height: 304,
  },
  flex: {
    display: 'flex',
  },
  between: {
    justifyContent: 'flex-between',
  },
  invalid: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    background:
      'linear-gradient(0deg, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)), rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(4px)',
    borderRadius: 16,
    padding: '23px 8px 8px',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 10,
  },
  invalidBtn: {
    borderRadius: 10,
    flex: 1,
    fontSize: 14,
    minHeight: 48,
    // mantis - 10956, 모바일에서도 x 버튼 나오도록 수정(Leehj)
    [theme.breakpoints.down(325)]: {
      minHeight: 48,
    },
  },
  upgradeBtn: {
    background: (props: any) => props.colors.primary['0'],
    border: (props: any) => `1px solid ${props.colors.primary['6']}`,
    backdropFilter: 'blur(4px)',
    color: (props: any) => props.colors.primary['7'],
  },
  removeBtn: {
    background: (props: any) => props.colors.primary['2'],
    border: (props: any) => `1px solid ${props.colors.primary['4']}`,
    backdropFilter: 'blur(4px)',
    color: (props: any) => props.colors.primary['0'],
    // mantis - 10956, 모바일에서도 x 버튼 나오도록 수정(Leehj)
    [theme.breakpoints.down(325)]: {
      whiteSpace: 'nowrap',
    },
    '&.MuiButton-contained:hover': {
      backgroundColor: (props: any) => props.colors.primary['2'],
    },
  },
  lottieDiv: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    zIndex: 0,
  },
}));

export interface RenewalCameraItemProps {
  camera: ICameraInfo;
  onClickMore: () => void;
  newFw?: boolean;
  handleMenu: (menu: APP_MENU) => void;
  onClickLocation: () => void;
  onClickManage?: () => void;
  onClickDelete?: (camera: ICameraInfo) => void;
  batteryPercent?: number;
  batteryState?: string;
}

export const RenewalCameraItem = ({
  camera,
  onClickMore,
  newFw,
  handleMenu,
  onClickLocation,
  onClickManage,
  onClickDelete,
  batteryPercent,
  batteryState,
}: RenewalCameraItemProps) => {
  const { colors, color } = useSelector((state: RootState) => state[THEME]);
  const { userProfile } = useSelector((state: RootState) => state[USER]);
  const classes = useStyles({ colors });
  const { t } = useTranslation();

  const [openPanel, setOpenPanel] = useState(false);
  const [profileType, setProfileType] = useState<'img' | 'lottie'>();
  const { permission } = useSelector((state: RootState) => state[PERMISSION]);

  useEffect(() => {
    console.log(
      'RenewalCameraItem',
      camera?.psn,
      camera?.dev_name,
      camera?.profile_img_v2
    );
    if (camera?.profile_img_v2) {
      if (camera.profile_img_v2.indexOf('.json') > -1) {
        setProfileType('lottie');
      } else {
        setProfileType('img');
      }
    }
  }, [camera?.dev_name, camera.profile_img_v2, camera?.psn]);

  const isNoneCloudModel = useMemo(() => {
    return _.includes(
      _.filter(OLD_MODELS, (d) => !ENABLED_CLOUD_OLD_MODELS.includes(d)),
      camera?.model
    );
  }, [camera?.model]);

  const batteryPaired = useMemo(() => {
    const battery = camera.battery;

    return !(
      !battery ||
      camera.reg_category === 'wifi' ||
      (!camera.login_date && !battery)
    );
  }, [camera.battery, camera.login_date, camera.reg_category]);

  const playbackBtnMarkup = useMemo(() => {
    return (
      <RenewalCameraMenu
        icon={
          <svg
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M17.7208 10.8582L8.75222 5.69167C7.53336 5.00038 6.02344 5.87359 6.02344 7.27436V17.6255C6.02344 19.0263 7.53336 19.8995 8.75222 19.2082L17.7208 14.0417C18.9397 13.3504 18.9397 11.5858 17.7208 10.8945V10.8582Z'
              stroke={colors.primary['7']}
              strokeWidth='1.6'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        }
        title={
          <Typography
            category='Default'
            variant='Caption'
            style={{
              lineHeight: '1',
              marginTop: 1,
            }}
            htmlColor={colors.primary['1']}
          >
            {t('Remote Playback')}
          </Typography>
        }
        onClick={() => {
          handleMenu('PLAYBACK');
        }}
      />
    );
  }, [colors.primary, handleMenu, t]);

  const liveviewBtnMarkup = useMemo(() => {
    return (
      <RenewalCameraMenu
        icon={
          <svg
            width='25'
            height='24'
            viewBox='0 0 25 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <rect
              x='3.33325'
              y='7.5'
              width='16.5'
              height='10.5'
              rx='3.75'
              stroke='#0095E0'
              strokeWidth='1.5'
            />
            <circle
              cx='8.58325'
              cy='12.75'
              r='1.5'
              stroke='#DF434D'
              strokeWidth='1.5'
            />
            <circle
              cx='19.0833'
              cy='9'
              r='2.25'
              fill='#DF434D'
              stroke={colors.primary['0']}
              strokeWidth='1.5'
            />
          </svg>
        }
        title={
          <Typography
            category='Default'
            variant='Caption'
            style={{
              lineHeight: '1',
              marginTop: 1,
            }}
            htmlColor={colors.primary['1']}
          >
            {t('Remote Live View')}
          </Typography>
        }
        onClick={() => {
          handleMenu('LIVEVIEW');
        }}
      />
    );
  }, [colors.primary, handleMenu, t]);

  const notificationBtnMarkup = useMemo(() => {
    let notiText = 'Notifications';
    if (camera.new_notification > 99) {
      notiText = '99+';
    } else if (camera.new_notification > 0) {
      notiText = `${camera.new_notification}`;
    }

    let subIcon = <></>;
    if (camera.new_notification > 0) {
      subIcon = (
        <div
          style={{
            width: 4,
            height: 4,
            borderRadius: 3,
            backgroundColor: LightColors.secondary['11'],
            position: 'absolute',
            top: 0,
            left: 18,
          }}
        />
      );
    }

    return (
      <RenewalCameraMenu
        icon={
          <svg
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M17.7331 16.9143H6.26633L7.3235 15.8571L7.55782 15.6228V15.2914V10.8571C7.55782 8.76042 9.00879 7.00359 10.9727 6.53846L11.5883 6.39266V5.76V5.21143C11.5883 4.98278 11.7711 4.8 11.9997 4.8C12.2284 4.8 12.4111 4.98278 12.4111 5.21143V5.76V6.39266L13.0268 6.53846C14.9906 7.00359 16.4416 8.76042 16.4416 10.8571V15.2914V15.6228L16.6759 15.8571L17.7331 16.9143Z'
              stroke='#0095E0'
              strokeWidth='1.6'
            />
            <path
              d='M10.7 19C10.7 19.6478 11.2433 20.1719 11.95 20.1719C12.6409 20.1719 13.2 19.6478 13.2 19H10.7Z'
              stroke='#0095E0'
            />
          </svg>
        }
        subIcon={subIcon}
        title={notiText}
        onClick={() => {
          handleMenu('NOTIFICATIONS');
        }}
      />
    );
  }, [camera, handleMenu]);

  const cloudStorageBtnMarkup = useMemo(() => {
    return (
      <RenewalCameraMenu
        icon={
          <svg
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <mask id='path-1-inside-1_7622_40932' fill='white'>
              <path d='M18.1328 10.3594C19.2005 10.4375 20.112 10.8802 20.8672 11.6875C21.6224 12.4688 22 13.4062 22 14.5C22 15.6458 21.5833 16.6354 20.75 17.4688C19.9427 18.276 18.9661 18.6797 17.8203 18.6797H7C5.61979 18.6797 4.4349 18.1979 3.44531 17.2344C2.48177 16.2448 2 15.0599 2 13.6797C2 12.4557 2.42969 11.362 3.28906 10.3984C4.17448 9.40885 5.22917 8.84896 6.45312 8.71875C7 7.70312 7.76823 6.88281 8.75781 6.25781C9.7474 5.63281 10.8281 5.32031 12 5.32031C13.4062 5.32031 14.7083 5.8151 15.9062 6.80469C17.1302 7.79427 17.8724 8.97917 18.1328 10.3594Z' />
            </mask>
            <path
              d='M18.1328 10.3594L16.6588 10.6375L16.8727 11.7712L18.0233 11.8554L18.1328 10.3594ZM20.8672 11.6875L19.7718 12.7122L19.7802 12.7212L19.7887 12.73L20.8672 11.6875ZM20.75 17.4688L21.8107 18.5294L20.75 17.4688ZM3.44531 17.2344L2.37061 18.2808L2.38456 18.2951L2.39889 18.3091L3.44531 17.2344ZM3.28906 10.3984L2.1712 9.39824L2.16962 9.40002L3.28906 10.3984ZM6.45312 8.71875L6.6118 10.2103L7.39867 10.1266L7.77383 9.4299L6.45312 8.71875ZM15.9062 6.80469L14.9509 7.96113L14.957 7.96616L14.9632 7.97113L15.9062 6.80469ZM18.0233 11.8554C18.7015 11.905 19.265 12.1705 19.7718 12.7122L21.9626 10.6628C20.959 9.58992 19.6995 8.97 18.2423 8.86337L18.0233 11.8554ZM19.7887 12.73C20.2715 13.2295 20.5 13.7924 20.5 14.5H23.5C23.5 13.0201 22.9732 11.708 21.9457 10.645L19.7887 12.73ZM20.5 14.5C20.5 15.2355 20.2503 15.8471 19.6893 16.4081L21.8107 18.5294C22.9163 17.4237 23.5 16.0562 23.5 14.5H20.5ZM19.6893 16.4081C19.1645 16.9329 18.57 17.1797 17.8203 17.1797V20.1797C19.3623 20.1797 20.7209 19.6192 21.8107 18.5294L19.6893 16.4081ZM17.8203 17.1797H7V20.1797H17.8203V17.1797ZM7 17.1797C6.011 17.1797 5.20148 16.8507 4.49174 16.1597L2.39889 18.3091C3.66832 19.5451 5.22858 20.1797 7 20.1797V17.1797ZM4.52002 16.188C3.82896 15.4782 3.5 14.6687 3.5 13.6797H0.5C0.5 15.4511 1.13458 17.0114 2.37061 18.2808L4.52002 16.188ZM3.5 13.6797C3.5 12.8455 3.77955 12.1021 4.40851 11.3969L2.16962 9.40002C1.07983 10.6219 0.5 12.066 0.5 13.6797H3.5ZM4.40692 11.3986C5.06578 10.6623 5.78791 10.298 6.6118 10.2103L6.29445 7.22717C4.67042 7.39994 3.28318 8.15544 2.1712 9.39825L4.40692 11.3986ZM7.77383 9.4299C8.19643 8.64507 8.78416 8.01529 9.5588 7.52605L7.95682 4.98958C6.7523 5.75033 5.80357 6.76118 5.13242 8.0076L7.77383 9.4299ZM9.5588 7.52605C10.3083 7.05265 11.1113 6.82031 12 6.82031V3.82031C10.545 3.82031 9.18645 4.21297 7.95682 4.98958L9.5588 7.52605ZM12 6.82031C13.0204 6.82031 13.9917 7.16876 14.9509 7.96113L16.8616 5.64824C15.4249 4.46145 13.7921 3.82031 12 3.82031V6.82031ZM14.9632 7.97113C15.946 8.76575 16.4724 9.64951 16.6588 10.6375L19.6068 10.0813C19.2724 8.30883 18.3144 6.82279 16.8493 5.63824L14.9632 7.97113Z'
              fill='#0095E0'
              mask='url(#path-1-inside-1_7622_40932)'
            />
          </svg>
        }
        title='Cloud Storage'
        onClick={() => {
          handleMenu('CLOUD_STORAGE');
        }}
      />
    );
  }, [handleMenu]);

  const reportBtnMarkup = useMemo(() => {
    return (
      <RenewalCameraMenu
        icon={
          <svg
            width='25'
            height='24'
            viewBox='0 0 25 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M19.6997 15.1147C19.1908 16.3186 18.3949 17.3794 17.3815 18.2045C16.3681 19.0295 15.1681 19.5937 13.8864 19.8476C12.6046 20.1016 11.2803 20.0376 10.029 19.6612C8.77773 19.2848 7.63767 18.6076 6.7085 17.6886C5.77933 16.7697 5.08933 15.637 4.69884 14.3897C4.30834 13.1424 4.22924 11.8183 4.46844 10.5334C4.70764 9.24838 5.25786 8.04158 6.071 7.01847C6.88414 5.99536 7.93544 5.18708 9.13299 4.66431'
              stroke='#0095E0'
              strokeWidth='1.6'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M20.3333 12.0018C20.3333 10.951 20.1264 9.91049 19.7244 8.93967C19.3224 7.96884 18.7332 7.08672 17.9904 6.34368C17.2476 5.60064 16.3658 5.01123 15.3953 4.6091C14.4248 4.20697 13.3847 4 12.3342 4V12.0018H20.3333Z'
              stroke='#DF434D'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        }
        title='Report'
        onClick={() => {
          handleMenu('REPORT');
        }}
      />
    );
  }, [handleMenu]);

  const batteryBtnMarkup = useMemo(() => {
    const icon = batteryPaired ? (
      <svg
        width='24'
        height='24'
        viewBox='0 0 24 24'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M3.87402 10.555C3.87402 9.63479 4.61917 8.88843 5.53933 8.88693L11.1611 8.87773C12.0834 8.87622 12.8319 9.62347 12.8319 10.5458V13.4597C12.8319 14.3799 12.0867 15.1262 11.1666 15.1277L5.54479 15.1369C4.62249 15.1384 3.87402 14.3912 3.87402 13.4689V10.555Z'
          fill='#0095E0'
        />
        <rect
          x='2.66567'
          y='7.66665'
          width='16.9991'
          height='8.6667'
          rx='2.6666'
          stroke='#0095E0'
          strokeWidth='1.3333'
        />
        <path
          d='M22.4533 12.1676C22.4533 13.1308 22.0359 13.5373 21.6835 13.7089C21.4352 13.8299 21.2034 13.6105 21.2034 13.3343V11.001C21.2034 10.7248 21.4352 10.5054 21.6835 10.6263C22.0359 10.798 22.4533 11.2045 22.4533 12.1676Z'
          fill='#0095E0'
        />
      </svg>
    ) : (
      <svg
        width='24'
        height='24'
        viewBox='0 0 24 24'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M20.37 12C20.37 16.6226 16.6226 20.37 12 20.37C7.37738 20.37 3.63 16.6226 3.63 12C3.63 7.37738 7.37738 3.63 12 3.63C16.6226 3.63 20.37 7.37738 20.37 12Z'
          stroke='#0095E0'
          strokeWidth='1.26'
        />
        <mask
          id='path-2-outside-1_7675_40872'
          maskUnits='userSpaceOnUse'
          x='7.6'
          y='7.59985'
          width='9'
          height='9'
          fill='black'
        >
          <rect fill='white' x='7.6' y='7.59985' width='9' height='9' />
          <path d='M15.725 12.7249H12.525V15.9249H11.475V12.7249H8.275V11.6749H11.475V8.47485H12.525V11.6749H15.725V12.7249Z' />
        </mask>
        <path
          d='M15.725 12.7249H12.525V15.9249H11.475V12.7249H8.275V11.6749H11.475V8.47485H12.525V11.6749H15.725V12.7249Z'
          fill='#0095E0'
        />
        <path
          d='M15.725 12.7249V12.9049H15.905V12.7249H15.725ZM12.525 12.7249V12.5449H12.345V12.7249H12.525ZM12.525 15.9249V16.1049H12.705V15.9249H12.525ZM11.475 15.9249H11.295V16.1049H11.475V15.9249ZM11.475 12.7249H11.655V12.5449H11.475V12.7249ZM8.275 12.7249H8.095V12.9049H8.275V12.7249ZM8.275 11.6749V11.4949H8.095V11.6749H8.275ZM11.475 11.6749V11.8549H11.655V11.6749H11.475ZM11.475 8.47485V8.29485H11.295V8.47485H11.475ZM12.525 8.47485H12.705V8.29485H12.525V8.47485ZM12.525 11.6749H12.345V11.8549H12.525V11.6749ZM15.725 11.6749H15.905V11.4949H15.725V11.6749ZM15.725 12.5449H12.525V12.9049H15.725V12.5449ZM12.345 12.7249V15.9249H12.705V12.7249H12.345ZM12.525 15.7449H11.475V16.1049H12.525V15.7449ZM11.655 15.9249V12.7249H11.295V15.9249H11.655ZM11.475 12.5449H8.275V12.9049H11.475V12.5449ZM8.455 12.7249V11.6749H8.095V12.7249H8.455ZM8.275 11.8549H11.475V11.4949H8.275V11.8549ZM11.655 11.6749V8.47485H11.295V11.6749H11.655ZM11.475 8.65485H12.525V8.29485H11.475V8.65485ZM12.345 8.47485V11.6749H12.705V8.47485H12.345ZM12.525 11.8549H15.725V11.4949H12.525V11.8549ZM15.545 11.6749V12.7249H15.905V11.6749H15.545Z'
          fill='#0095E0'
          mask='url(#path-2-outside-1_7675_40872)'
        />
      </svg>
    );
    return (
      <RenewalCameraMenu
        icon={icon}
        title={batteryPaired ? 'Battery' : 'Battery Pairing'}
        onClick={() => {
          handleMenu('BATTERY');
        }}
      />
    );
  }, [batteryPaired, handleMenu]);

  const moreBtnMarkup = useMemo(() => {
    return (
      <RenewalCameraMenu
        icon={
          <svg
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g clipPath='url(#clip0_7622_40998)'>
              <path
                d='M10.8281 10.8281C11.1667 10.4896 11.5573 10.3203 12 10.3203C12.4427 10.3203 12.8333 10.4896 13.1719 10.8281C13.5104 11.1667 13.6797 11.5573 13.6797 12C13.6797 12.4427 13.5104 12.8333 13.1719 13.1719C12.8333 13.5104 12.4427 13.6797 12 13.6797C11.5573 13.6797 11.1667 13.5104 10.8281 13.1719C10.4896 12.8333 10.3203 12.4427 10.3203 12C10.3203 11.5573 10.4896 11.1667 10.8281 10.8281ZM15.8281 10.8281C16.1667 10.4896 16.5573 10.3203 17 10.3203C17.4427 10.3203 17.8333 10.4896 18.1719 10.8281C18.5104 11.1667 18.6797 11.5573 18.6797 12C18.6797 12.4427 18.5104 12.8333 18.1719 13.1719C17.8333 13.5104 17.4427 13.6797 17 13.6797C16.5573 13.6797 16.1667 13.5104 15.8281 13.1719C15.4896 12.8333 15.3203 12.4427 15.3203 12C15.3203 11.5573 15.4896 11.1667 15.8281 10.8281ZM5.82812 10.8281C6.16667 10.4896 6.55729 10.3203 7 10.3203C7.44271 10.3203 7.83333 10.4896 8.17188 10.8281C8.51042 11.1667 8.67969 11.5573 8.67969 12C8.67969 12.4427 8.51042 12.8333 8.17188 13.1719C7.83333 13.5104 7.44271 13.6797 7 13.6797C6.55729 13.6797 6.16667 13.5104 5.82812 13.1719C5.48958 12.8333 5.32031 12.4427 5.32031 12C5.32031 11.5573 5.48958 11.1667 5.82812 10.8281Z'
                fill='#0095E0'
              />
            </g>
            {(newFw || camera?.require_time_sync) && (
              <circle cx='22' cy='2' r='2' fill='#DF434D' />
            )}
            <defs>
              <clipPath id='clip0_7622_40998'>
                <rect
                  width='20'
                  height='20'
                  fill='white'
                  transform='translate(2 2)'
                />
              </clipPath>
            </defs>
          </svg>
        }
        title='More'
        onClick={onClickMore}
      />
    );
  }, [camera?.require_time_sync, newFw, onClickMore]);

  const gpsTrackingBtnMarkup = useMemo(() => {
    let hasPermission = false;

    if (!userProfile?.userType) {
      hasPermission = permission.gpsTrackingPerm;
    } else if (userProfile?.userType === 'Master') {
      hasPermission = true;
    } else if (userProfile?.userType === 'SubMaster') {
      if (permission.gpsTrackingPerm) {
        hasPermission = true;
      }
    }

    return (
      <RenewalCameraMenu
        icon={
          <svg
            width='25'
            height='24'
            viewBox='0 0 25 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g clipPath='url(#clip0_7622_40960)'>
              <path
                d='M7.26753 19.8C8.26164 19.8 9.06753 18.9941 9.06753 18C9.06753 17.0058 8.26164 16.2 7.26753 16.2C6.27342 16.2 5.46753 17.0058 5.46753 18C5.46753 18.9941 6.27342 19.8 7.26753 19.8Z'
                stroke='#0095E0'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M18.0683 7.79995C19.0624 7.79995 19.8683 6.99406 19.8683 5.99995C19.8683 5.00584 19.0624 4.19995 18.0683 4.19995C17.0742 4.19995 16.2683 5.00584 16.2683 5.99995C16.2683 6.99406 17.0742 7.79995 18.0683 7.79995Z'
                stroke='#DF434D'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M12.8267 18H16.7867C17.6036 18 18.387 17.6839 18.9646 17.1213C19.5423 16.5587 19.8667 15.7956 19.8667 15C19.8667 14.2044 19.5423 13.4413 18.9646 12.8787C18.387 12.3161 17.6036 12 16.7867 12H9.74675C8.92988 12 8.14647 11.6839 7.56886 11.1213C6.99125 10.5587 6.66675 9.79565 6.66675 9C6.66675 8.20435 6.99125 7.44129 7.56886 6.87868C8.14647 6.31607 8.92988 6 9.74675 6H12.8267'
                stroke='#0095E0'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <g filter='url(#filter0_b_7622_40960)'>
                <path
                  d='M7.15672 8.80005C4.91689 8.80005 3.06519 10.5911 3.06519 12.8069C3.06519 13.9236 3.47079 14.9602 4.13968 15.8389C4.87075 16.8081 5.7796 17.6525 6.80843 18.3154C7.0654 18.4801 7.2767 18.4685 7.51084 18.3154C8.53384 17.6525 9.4427 16.8081 10.1801 15.8389C10.8485 14.9602 11.2546 13.9236 11.2546 12.8069C11.2546 10.5911 9.40286 8.80005 7.15672 8.80005Z'
                  fill='#DF434D'
                />
              </g>
              <g filter='url(#filter1_bd_7622_40960)'>
                <mask id='path-5-inside-1_7622_40960' fill='white'>
                  <path d='M7.12654 14.2575C6.38382 14.2575 5.77808 13.6736 5.77808 12.9313C5.77808 12.1948 6.38382 11.5825 7.12654 11.5825C7.86975 11.5825 8.48132 12.1948 8.48132 12.9313C8.48132 13.6736 7.86975 14.2575 7.12654 14.2575Z' />
                </mask>
                <path
                  d='M7.12654 14.2575C6.38382 14.2575 5.77808 13.6736 5.77808 12.9313C5.77808 12.1948 6.38382 11.5825 7.12654 11.5825C7.86975 11.5825 8.48132 12.1948 8.48132 12.9313C8.48132 13.6736 7.86975 14.2575 7.12654 14.2575Z'
                  fill='white'
                />
                <path
                  d='M7.12654 14.1075C6.4634 14.1075 5.92808 13.5876 5.92808 12.9313H5.62808C5.62808 13.7597 6.30423 14.4075 7.12654 14.4075V14.1075ZM5.92808 12.9313C5.92808 12.2771 6.46715 11.7325 7.12654 11.7325V11.4325C6.30048 11.4325 5.62808 12.1124 5.62808 12.9313H5.92808ZM7.12654 11.7325C7.78729 11.7325 8.33132 12.278 8.33132 12.9313H8.63132C8.63132 12.1116 7.95221 11.4325 7.12654 11.4325V11.7325ZM8.33132 12.9313C8.33132 13.5867 7.79105 14.1075 7.12654 14.1075V14.4075C7.94845 14.4075 8.63132 13.7606 8.63132 12.9313H8.33132Z'
                  fill='url(#paint0_linear_7622_40960)'
                  fillOpacity='0.5'
                  mask='url(#path-5-inside-1_7622_40960)'
                />
              </g>
            </g>
            <defs>
              <filter
                id='filter0_b_7622_40960'
                x='-14.9348'
                y='-9.19995'
                width='44.1895'
                height='45.6345'
                filterUnits='userSpaceOnUse'
                colorInterpolationFilters='sRGB'
              >
                <feFlood floodOpacity='0' result='BackgroundImageFix' />
                <feGaussianBlur in='BackgroundImageFix' stdDeviation='9' />
                <feComposite
                  in2='SourceAlpha'
                  operator='in'
                  result='effect1_backgroundBlur_7622_40960'
                />
                <feBlend
                  mode='normal'
                  in='SourceGraphic'
                  in2='effect1_backgroundBlur_7622_40960'
                  result='shape'
                />
              </filter>
              <filter
                id='filter1_bd_7622_40960'
                x='-5.47192'
                y='0.33252'
                width='25.2031'
                height='25.175'
                filterUnits='userSpaceOnUse'
                colorInterpolationFilters='sRGB'
              >
                <feFlood floodOpacity='0' result='BackgroundImageFix' />
                <feGaussianBlur in='BackgroundImageFix' stdDeviation='5.625' />
                <feComposite
                  in2='SourceAlpha'
                  operator='in'
                  result='effect1_backgroundBlur_7622_40960'
                />
                <feColorMatrix
                  in='SourceAlpha'
                  type='matrix'
                  values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                  result='hardAlpha'
                />
                <feOffset dx='3.75' dy='3.75' />
                <feGaussianBlur stdDeviation='3.75' />
                <feColorMatrix
                  type='matrix'
                  values='0 0 0 0 0.15375 0 0 0 0 0.522433 0 0 0 0 0.9 0 0 0 0.5 0'
                />
                <feBlend
                  mode='normal'
                  in2='effect1_backgroundBlur_7622_40960'
                  result='effect2_dropShadow_7622_40960'
                />
                <feBlend
                  mode='normal'
                  in='SourceGraphic'
                  in2='effect2_dropShadow_7622_40960'
                  result='shape'
                />
              </filter>
              <linearGradient
                id='paint0_linear_7622_40960'
                x1='6.06927'
                y1='12.4798'
                x2='8.3647'
                y2='12.4926'
                gradientUnits='userSpaceOnUse'
              >
                <stop stopColor='white' />
                <stop offset='1' stopColor='white' stopOpacity='0' />
              </linearGradient>
              <clipPath id='clip0_7622_40960'>
                <rect
                  width='24'
                  height='24'
                  fill='white'
                  transform='translate(0.666748)'
                />
              </clipPath>
            </defs>
          </svg>
        }
        subIcon={
          !hasPermission ? (
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M14.8047 9.67578V8.5C14.8047 8.00781 14.6224 7.58854 14.2578 7.24219C13.9114 6.8776 13.4922 6.69531 13 6.69531C12.5078 6.69531 12.0794 6.8776 11.7148 7.24219C11.3685 7.58854 11.1953 8.00781 11.1953 8.5V9.67578H14.8047ZM12.1797 14.5703C12.4167 14.8073 12.6901 14.9258 13 14.9258C13.3099 14.9258 13.5833 14.8073 13.8203 14.5703C14.0573 14.3333 14.1758 14.0599 14.1758 13.75C14.1758 13.4401 14.0573 13.1667 13.8203 12.9297C13.5833 12.6927 13.3099 12.5742 13 12.5742C12.6901 12.5742 12.4167 12.6927 12.1797 12.9297C11.9427 13.1667 11.8242 13.4401 11.8242 13.75C11.8242 14.0599 11.9427 14.3333 12.1797 14.5703ZM16.5 9.67578C16.8099 9.67578 17.0833 9.79427 17.3203 10.0312C17.5573 10.25 17.6758 10.5143 17.6758 10.8242V16.6758C17.6758 16.9857 17.5573 17.2591 17.3203 17.4961C17.0833 17.7148 16.8099 17.8242 16.5 17.8242H9.49998C9.19009 17.8242 8.91665 17.7148 8.67967 17.4961C8.44269 17.2591 8.3242 16.9857 8.3242 16.6758V10.8242C8.3242 10.5143 8.44269 10.25 8.67967 10.0312C8.91665 9.79427 9.19009 9.67578 9.49998 9.67578H10.0742V8.5C10.0742 7.69792 10.3568 7.01432 10.9219 6.44922C11.5052 5.86589 12.1979 5.57422 13 5.57422C13.8021 5.57422 14.4857 5.86589 15.0508 6.44922C15.6341 7.01432 15.9258 7.69792 15.9258 8.5V9.67578H16.5Z'
                fill='#0095E0'
              />
            </svg>
          ) : undefined
        }
        title='GPS Tracking'
        onClick={() => {
          handleMenu('GPS_TRACKING');
        }}
      />
    );
  }, [handleMenu, permission.gpsTrackingPerm, userProfile?.userType]);

  const visibleButtons = useMemo(() => {
    const buttons: any[] = [playbackBtnMarkup];

    if (!isNoneCloudModel && camera.reg_category !== 'wifi') {
      buttons.push(liveviewBtnMarkup);
    }

    if (camera.reg_category !== 'wifi') {
      buttons.push(notificationBtnMarkup);
    }

    buttons.push(cloudStorageBtnMarkup);

    if (camera.reg_category !== 'wifi') {
      buttons.push(reportBtnMarkup);
      buttons.push(gpsTrackingBtnMarkup);
    }

    buttons.push(batteryBtnMarkup);

    buttons.push(moreBtnMarkup);

    return buttons;
  }, [
    batteryBtnMarkup,
    camera.reg_category,
    cloudStorageBtnMarkup,
    gpsTrackingBtnMarkup,
    isNoneCloudModel,
    liveviewBtnMarkup,
    moreBtnMarkup,
    notificationBtnMarkup,
    playbackBtnMarkup,
    reportBtnMarkup,
  ]);

  const buttonsMarkup = useMemo(() => {
    const markups: any[] = [];
    const chunk = _.chunk(visibleButtons, 3);
    for (let i = 0; i < chunk.length; i++) {
      const btns = chunk[i];
      let length = btns.length;
      let emptyDivLength = 3 - length;

      markups.push(
        <div
          key={`camera-btn-div-${camera.psn}-${i}`}
          className={classes.btnContainer}
        >
          {_.map(btns, (b, j) => (
            <React.Fragment key={`camera-btn-${camera.psn}-${i}-${j}`}>
              {b}
            </React.Fragment>
          ))}
          {_.range(0, emptyDivLength).map((j) => (
            <div
              key={`empty-btn-${camera.psn}-${i}-${j}`}
              style={{ flex: '1 1 4.74%' }}
            ></div>
          ))}
        </div>
      );
    }

    return markups;
  }, [camera.psn, classes.btnContainer, visibleButtons]);

  return (
    <div
      style={{
        backgroundColor:
          color === 'dark' ? SemanticDarkColors.primary['1'] : 'white',
        border: `1px solid ${colors.primary['6']}`,
        boxShadow:
          '0px 2px 2px rgba(0, 0, 0, 0.06), 0px 6px 40px rgba(0, 0, 0, 0.05)',
        borderRadius: 16,
        width: '100%',
        position: 'relative',
      }}
    >
      <div
        style={{
          height: 80,
          backgroundColor: colors.primary['6'],
          ...(profileType === 'img'
            ? {
                backgroundImage: `url(${camera?.profile_img_v2})`,
              }
            : {
                backgroundImage: `url(${
                  color === 'dark'
                    ? '/images/default-profile-dark.png'
                    : '/images/default-profile-light.png'
                })`,
              }),

          borderRadius: '14px 14px 0 0',
          display: 'flex',
          alignItems: 'flex-end',
          position: 'relative',
          backgroundSize: 'contain',
          overflow: 'hidden',
        }}
        onClick={() => {
          console.log('onLocation');
          onClickLocation();
        }}
      >
        {profileType === 'img' && (
          <div
            className={classes.lottieDiv}
            style={{
              background:
                'linear-gradient(180deg, rgba(0, 0, 0, 0.0425) 0%, rgba(19, 19, 28, 0.85) 100%)',
            }}
          />
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            maxWidth: '100%',
            zIndex: 1,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {getRenewalCloudConnectivityIcon(
              camera,
              colors,
              profileType === 'img',
              color === 'dark'
            )}
            <Typography
              category='Default'
              variant='SmallBold'
              htmlColor={profileType === 'img' ? 'white' : colors.primary['1']}
              style={{
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                width: '50vw',
                maxWidth: '80%',
              }}
            >
              {camera.dev_name}
            </Typography>
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g clipPath='url(#clip0_11192_125774)'>
                <path
                  d='M8.66406 9.08594C9.03906 9.4375 9.48438 9.61328 10 9.61328C10.5156 9.61328 10.9492 9.4375 11.3008 9.08594C11.6758 8.71094 11.8633 8.26562 11.8633 7.75C11.8633 7.23438 11.6758 6.80078 11.3008 6.44922C10.9492 6.07422 10.5156 5.88672 10 5.88672C9.48438 5.88672 9.03906 6.07422 8.66406 6.44922C8.3125 6.80078 8.13672 7.23438 8.13672 7.75C8.13672 8.26562 8.3125 8.71094 8.66406 9.08594ZM6.27344 4.05859C7.30469 3.02734 8.54688 2.51172 10 2.51172C11.4531 2.51172 12.6836 3.02734 13.6914 4.05859C14.7227 5.06641 15.2383 6.29688 15.2383 7.75C15.2383 8.47656 15.0508 9.30859 14.6758 10.2461C14.3242 11.1836 13.8906 12.0625 13.375 12.8828C12.8594 13.7031 12.3438 14.4766 11.8281 15.2031C11.3359 15.9062 10.9141 16.4688 10.5625 16.8906L10 17.4883C9.85938 17.3242 9.67188 17.1133 9.4375 16.8555C9.20312 16.5742 8.78125 16.0352 8.17188 15.2383C7.5625 14.418 7.02344 13.6328 6.55469 12.8828C6.10938 12.1094 5.69922 11.2422 5.32422 10.2812C4.94922 9.32031 4.76172 8.47656 4.76172 7.75C4.76172 6.29688 5.26562 5.06641 6.27344 4.05859Z'
                  fill='#0095E0'
                />
              </g>
              <defs>
                <clipPath id='clip0_11192_125774'>
                  <rect width='20' height='20' fill='white' />
                </clipPath>
              </defs>
            </svg>
          </div>

          {((batteryPercent && batteryState) || batteryPaired) && (
            <div className={clsx([classes.BatteryStatusButton])}>
              <BatteryStatusButton
                isLottie={profileType === 'lottie'}
                camera={camera}
                batteryPercent={batteryPercent}
                batteryState={batteryState}
                setPanelState={() => {}}
                onClick={() => {}}
              />
            </div>
          )}
        </div>
      </div>
      <div
        className={clsx(classes.rootTransition, {
          [classes.panelClose]: !openPanel,
        })}
        style={{
          borderBottom: `1px solid ${colors.primary['6']}`,
          borderRadius: '0 0 16px 16px',
          padding: 6,
          position: 'relative',
          overflow: 'hidden',
          ...(openPanel
            ? { height: 130 + (_.chunk(visibleButtons, 3).length - 1) * 86 }
            : {}),
        }}
      >
        {buttonsMarkup}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            position: 'absolute',
            bottom: 0,
            backgroundColor:
              color === 'dark' ? SemanticDarkColors.primary['1'] : 'white',
            left: 0,
            right: 0,
            borderRadius: 16,
            paddingBottom: 2,
          }}
        >
          <div
            style={{
              width: 48,
              display: 'flex',
              height: 36,
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
            onClick={() => {
              setOpenPanel((p) => !p);
            }}
          >
            {(newFw || camera?.require_time_sync) && (
              <div
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: 3,
                  backgroundColor: LightColors.secondary['11'],
                  position: 'absolute',
                  top: 0,
                  right: 0,
                }}
              />
            )}
            <svg
              width='23'
              height='8'
              viewBox='0 0 23 8'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              style={{ transform: openPanel ? 'rotate(180deg)' : '' }}
            >
              <path
                d='M1.56641 3.82422C1.25391 3.67578 0.996095 3.46875 0.79297 3.20312C0.589845 2.9375 0.488282 2.62891 0.488282 2.27734C0.488282 1.69922 0.671876 1.23437 1.03906 0.882812C1.40625 0.531249 1.83203 0.355468 2.31641 0.355468C2.59766 0.355468 2.89844 0.421874 3.21875 0.554687C3.54688 0.679686 3.85547 0.804686 4.14453 0.929686L11.9609 4.28125L10.7773 4.28125L18.6055 0.929684C18.8867 0.804684 19.1914 0.679684 19.5195 0.554684C19.8477 0.421871 20.1484 0.355465 20.4219 0.355465C20.9063 0.355465 21.332 0.531246 21.6992 0.882808C22.0664 1.23437 22.25 1.69921 22.25 2.27734C22.25 2.6289 22.1484 2.9375 21.9453 3.20312C21.7422 3.46875 21.4844 3.67578 21.1719 3.82421L13.4141 7.16406C13.0313 7.32812 12.6836 7.46875 12.3711 7.58593C12.0586 7.69531 11.7266 7.75 11.375 7.75C11.0156 7.75 10.6797 7.69531 10.3672 7.58594C10.0547 7.46875 9.71094 7.32812 9.33594 7.16406L1.56641 3.82422Z'
                fill={openPanel ? colors.primary['7'] : '#BEBEC1'}
              />
            </svg>
          </div>
        </div>
      </div>
      {camera.valid === 'invalid' && camera.reg_category !== 'wifi' && (
        <div className={classes.invalid}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flex: 1,
            }}
          >
            <div
              style={{
                padding: '0 8px',
                marginTop: 14,
              }}
            >
              <svg
                width='48'
                height='48'
                viewBox='0 0 48 48'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M18.1016 20.9766H15.5C13.8594 20.9766 12.4531 21.5859 11.2812 22.8047C10.1094 23.9766 9.52344 25.3828 9.52344 27.0234C9.52344 28.6641 10.1094 30.0703 11.2812 31.2422C12.4531 32.4141 13.8594 33 15.5 33H30.125L18.1016 20.9766ZM11 13.875L12.8984 11.9766L38 37.0781L36.1016 38.9766L33.0781 36.0234H15.5C13.0156 36.0234 10.8828 35.1562 9.10156 33.4219C7.36719 31.6406 6.5 29.5078 6.5 27.0234C6.5 24.5859 7.34375 22.5 9.03125 20.7656C10.7187 19.0312 12.7578 18.1172 15.1484 18.0234L11 13.875ZM35.5391 21.0469C37.4609 21.1875 39.1016 21.9844 40.4609 23.4375C41.8203 24.8437 42.5 26.5312 42.5 28.5C42.5 31.0781 41.4453 33.1172 39.3359 34.6172L37.1563 32.4375C38.7031 31.5937 39.4766 30.2812 39.4766 28.5C39.4766 27.2812 39.0313 26.2266 38.1406 25.3359C37.25 24.4453 36.1953 24 34.9766 24H32.7266V23.2266C32.7266 20.9297 31.9297 18.9844 30.3359 17.3906C28.7422 15.7969 26.7969 15 24.5 15C23.1875 15 21.9219 15.3047 20.7031 15.9141L18.4531 13.7344C20.2812 12.5625 22.2969 11.9766 24.5 11.9766C27.0313 11.9766 29.375 12.8672 31.5313 14.6484C33.7344 16.4297 35.0703 18.5625 35.5391 21.0469Z'
                  fill='white'
                />
              </svg>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
              }}
            >
              <Typography
                category='Default'
                variant='Small'
                htmlColor={LightColors.primary['0']}
              >
                {t('Your previous plan is expired.')}
              </Typography>
              <Typography
                category='Default'
                variant='BodyBold'
                htmlColor={LightColors.primary['0']}
              >
                {t('Upgrade the plan to restore the camera')}
              </Typography>
              <Typography
                category='Default'
                variant='BodyBold'
                htmlColor={LightColors.primary['0']}
              >
                ({camera.dev_name})
              </Typography>
            </div>
          </div>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}
          >
            <Button
              className={clsx(classes.invalidBtn, classes.upgradeBtn)}
              onClick={(e) => {
                e.stopPropagation();
                onClickManage?.();
                // onUpgrade();
              }}
            >
              {t('Upgrade')}
            </Button>

            <Button
              className={clsx(classes.invalidBtn, classes.removeBtn)}
              onClick={(e) => {
                e.stopPropagation();
                onClickDelete?.(camera);
                // onRemove?.(camera);
              }}
            >
              {t('Deactivate Cloud')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
